import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaGithub,
  FaLinkedin,
  FaEnvelope,
  FaFileDownload,
} from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import emailjs from '@emailjs/browser';
const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );

      setFormStatus({
        type: "success",
        message: "Message sent successfully. I’ll get back to you soon.",
      });

      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      setFormStatus({
        type: "error",
        message: "Something went wrong. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setFormStatus({ type: "", message: "" }), 5000);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const socialLinks = [
    {
      name: "GitHub",
      url: process.env.REACT_APP_GITHUB_URL,
      icon: <FaGithub />,
    },
    {
      name: "LinkedIn",
      url: process.env.REACT_APP_LINKEDIN_URL,
      icon: <FaLinkedin />,
    },
    {
      name: "LeetCode",
      url: process.env.REACT_APP_LEETCODE_URL,
      icon: <SiLeetcode />,
    },
    {
      name: "Email",
      url: `mailto:${process.env.REACT_APP_EMAIL}`,
      icon: <FaEnvelope />,
    },
    {
      name: "Resume",
      url: process.env.REACT_APP_RESUME_URL,
      icon: <FaFileDownload />,
      isResume: true,
    },
  ];

  return (
    <section id="contact" className="section-padding bg-bg-secondary">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="max-w-4xl mx-auto"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black gradient-text mb-6">
            Let's Connect
          </h2>
          <p className="text-lg sm:text-xl text-text-secondary font-light max-w-2xl mx-auto">
            Have a project in mind? Let's work together!
          </p>
        </motion.div>

        <motion.form
          variants={itemVariants}
          onSubmit={handleSubmit}
          className="bg-bg-tertiary p-8 sm:p-12 rounded-3xl border border-[rgba(76,201,240,0.1)] mb-12"
        >
          <div className="space-y-8">
            <div>
              <label
                htmlFor="name"
                className="block mb-2 text-text-primary font-medium text-sm"
              >
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-4 bg-bg-secondary border-2 border-[rgba(76,201,240,0.1)] rounded-xl text-text-primary font-body text-base transition-all duration-300 focus:outline-none focus:border-accent-primary focus:shadow-[0_0_0_3px_rgba(76,201,240,0.1)] focus:-translate-y-0.5"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-text-primary font-medium text-sm"
              >
                Your Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-4 bg-bg-secondary border-2 border-[rgba(76,201,240,0.1)] rounded-xl text-text-primary font-body text-base transition-all duration-300 focus:outline-none focus:border-accent-primary focus:shadow-[0_0_0_3px_rgba(76,201,240,0.1)] focus:-translate-y-0.5"
              />
            </div>

            <div>
              <label
                htmlFor="subject"
                className="block mb-2 text-text-primary font-medium text-sm"
              >
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-4 bg-bg-secondary border-2 border-[rgba(76,201,240,0.1)] rounded-xl text-text-primary font-body text-base transition-all duration-300 focus:outline-none focus:border-accent-primary focus:shadow-[0_0_0_3px_rgba(76,201,240,0.1)] focus:-translate-y-0.5"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block mb-2 text-text-primary font-medium text-sm"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="w-full px-4 py-4 bg-bg-secondary border-2 border-[rgba(76,201,240,0.1)] rounded-xl text-text-primary font-body text-base transition-all duration-300 focus:outline-none focus:border-accent-primary focus:shadow-[0_0_0_3px_rgba(76,201,240,0.1)] focus:-translate-y-0.5 resize-y"
              ></textarea>
            </div>

            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-5 bg-gradient-to-r from-accent-primary to-accent-secondary text-bg-primary font-bold rounded-full text-lg uppercase tracking-wider transition-all duration-400 hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(76,201,240,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </motion.button>

            {formStatus.message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-4 p-4 rounded-xl text-center font-medium ${
                  formStatus.type === "success"
                    ? "bg-[rgba(76,201,240,0.1)] text-accent-primary border border-accent-primary"
                    : "bg-[rgba(255,0,110,0.1)] text-accent-tertiary border border-accent-tertiary"
                }`}
              >
                {formStatus.message}
              </motion.div>
            )}
          </div>
        </motion.form>

        <motion.div
          variants={containerVariants}
          className="flex gap-6 justify-center flex-wrap"
        >
          {socialLinks.map((link, index) => (
            <motion.a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={itemVariants}
              whileHover={{
                y: -5,
                backgroundColor: link.isResume
                  ? "var(--accent-secondary)"
                  : "var(--accent-primary)",
                color: "var(--bg-primary)",
                scale: link.isResume ? 1.05 : 1,
              }}
              className={`px-7 py-3 ${
                link.isResume
                  ? "bg-accent-primary text-bg-primary"
                  : "bg-bg-primary text-accent-primary border-2 border-[rgba(76,201,240,0.3)]"
              } rounded-full font-semibold text-sm tracking-wider transition-all duration-400 hover:shadow-[0_15px_25px_rgba(76,201,240,0.4)] flex items-center gap-2`}
            >
              <span className="text-lg">{link.icon}</span>
              {link.name}
            </motion.a>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Contact;
